import { X } from "lucide-react";
import { useTranslation } from "react-i18next";

import { OverrideBadges } from "./OverrideBadges";
import { VacancyOverrideCard } from "./VacancyOverrideCard";
import { OVERRIDE_STATE, type OverrideRow } from "../overrides";

import { Button } from "@/components/ui/v2/button";

type OverrideItemProps = {
  row: OverrideRow;
  readOnly: boolean;
  isAffected: boolean;
  onKeep: () => void;
  onRemove: () => void;
};

export function OverrideItem({
  row,
  readOnly,
  isAffected,
  onKeep,
  onRemove,
}: OverrideItemProps) {
  const { t } = useTranslation();
  const removeLabel = t("admin.segments.builder.exceptions.removeException");

  const badges = (
    <OverrideBadges
      type={row.type}
      isRedundant={row.state === OVERRIDE_STATE.REDUNDANT}
      isOffline={row.state === OVERRIDE_STATE.OFFLINE}
      isUnpublished={row.state === OVERRIDE_STATE.UNPUBLISHED}
      isAffected={isAffected}
    />
  );
  const tone = isAffected ? "affected" : "default";

  if (readOnly)
    return (
      <VacancyOverrideCard vacancy={row.vacancy} badges={badges} tone={tone} />
    );

  if (isAffected) {
    return (
      <VacancyOverrideCard
        vacancy={row.vacancy}
        badges={badges}
        tone={tone}
        action={
          <>
            <Button type="button" variant="ghost" size="sm" onClick={onKeep}>
              {t("admin.segments.builder.exceptions.keep")}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onRemove}
            >
              {t("admin.segments.builder.exceptions.remove")}
            </Button>
          </>
        }
      />
    );
  }

  return (
    <VacancyOverrideCard
      vacancy={row.vacancy}
      badges={badges}
      action={
        <Button
          type="button"
          variant="ghost-destructive"
          size="icon"
          aria-label={removeLabel}
          title={removeLabel}
          onClick={onRemove}
        >
          <X />
        </Button>
      }
    />
  );
}
